import { useContext } from "react";

import { Checkbox, IconButton, Table } from "@narmi/design_system";
import { format, parseISO } from "date-fns";
import { useAccount } from "byzantine/src/dbbl/businessLogic/entities";

import { CheckboxContext } from "../components/CheckboxContext";

import styles from "./DocumentsTable.module.scss";

import type { WebDocument } from "byzantine/src/dbbl/businessLogic/entities/documents/types";

const formatDocumentDate = (createdAt: string) => {
  const dateOnlyValue = createdAt.split("T")[0];

  return format(parseISO(dateOnlyValue), "MMM d, yyyy");
};

const DocumentRow = ({
  doc,
  toggleCheckbox,
  isHtmlJson,
}: {
  doc: WebDocument;
  toggleCheckbox: (doc: WebDocument) => void;
  isHtmlJson: boolean;
}) => {
  const { checkBoxState } = useContext(CheckboxContext);
  const account = useAccount(doc.account_id);
  const viewUrl = isHtmlJson
    ? doc.www_url
    : `${doc.www_download_url}?download=0`;
  const accountDescription =
    account?.getShortDescription(false) || doc.description;
  return (
    <Table.Row
      data-testid={`document-row-${doc.id}`}
      onRowClick={() => toggleCheckbox(doc)}
    >
      <Table.Cell>
        <div
          className={styles["documents-page-table-checkbox"]}
          // We prevent propagation here so when toggling the checkbox
          // via space bar, we prevent toggleCheckbox from being called twice.
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            kind="table"
            label={`${doc.type_label} - ${accountDescription}`}
            checked={checkBoxState[doc.id] !== undefined}
            onChange={() => toggleCheckbox(doc)}
          />
        </div>
        <a
          className={styles["documents-page-table-link"]}
          href={viewUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`document-link-${doc.id}`}
          // If they click this text prevent clicking the row & toggling the checkbox
          onClick={(e) => e.stopPropagation()}
        >
          <span className="narmi-icon-file-text1" />
          <span
            className={styles["documents-page-table-link-text"]}
            data-testid={`document-type-label-${doc.id}`}
          >
            {doc.type_label}
          </span>
        </a>
      </Table.Cell>
      <Table.Cell>
        <span data-testid={`document-description-${doc.id}`}>
          {accountDescription}
        </span>
      </Table.Cell>
      <Table.Cell>
        <span data-testid={`document-date-${doc.id}`}>
          {formatDocumentDate(doc.created_at)}
        </span>
      </Table.Cell>
      <Table.Cell> </Table.Cell>
      <Table.Cell textAlign="end">
        <IconButton
          kind="action"
          name="download"
          textSize="m"
          as="a"
          onClick={(e: React.MouseEvent<HTMLAnchorElement>) =>
            e.stopPropagation()
          }
          href={doc.www_download_url}
        />
      </Table.Cell>
    </Table.Row>
  );
};

export default DocumentRow;
